import { Component, Injector, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation } from "@angular/core";
import { AppComponentBase } from "@shared/common/app-component-base";
import { AppConsts } from "@shared/AppConsts";
import {
    ClientDto,
    ClientServiceProxy,
    ClientType2,
    UserListDto,
    BeginIntegrationOAuthInput,
    ConnectDevOpsProjectInput,
    ConnectIntegrationApiKeyInput,
    DevOpsProjectConnectionDto,
    IntegrationAuthType,
    IntegrationCatalogItemDto,
    IntegrationConnectionDto,
    IntegrationLicenseAllocation,
    IntegrationProvider,
    IntegrationServiceProxy,
    OutlookCalendarServiceProxy,
    OutlookLinkedAccountDto,
    UserServiceProxy,
    DisconnectDevOpsProjectInput,
} from "@shared/service-proxies/service-proxies";
import { finalize } from "rxjs/operators";
import {
    clearOutlookOAuthResultFromStorage,
    KEK_OAUTH_POPUP_NAME,
    OUTLOOK_OAUTH_POPUP_NAME,
    OUTLOOK_OAUTH_STORAGE_KEY,
    readOutlookOAuthResultFromStorage,
} from "@app/shared/utils/outlook-oauth-relay";

@Component({
    standalone: false,
    selector: "app-profile-integrations",
    templateUrl: "./profile-integrations.component.html",
    styleUrls: ["./profile-integrations.component.scss"],
    encapsulation: ViewEncapsulation.None,
})
export class ProfileIntegrationsComponent extends AppComponentBase implements OnInit, OnChanges, OnDestroy {
    /** When set (e.g. Member Allocation), load that member's catalog instead of the signed-in user. */
    @Input() userId: number;

    /** Member Allocation is view-only: show Linked when connected, never Link, Delink, or Coming Soon. */
    @Input() readOnly = false;

    catalog: IntegrationCatalogItemDto[] = [];
    clients: ClientDto[] = [];
    loading = false;
    connecting = false;
    selectedItem: IntegrationCatalogItemDto;
    licenseAllocation: IntegrationLicenseAllocation = IntegrationLicenseAllocation.MyCompany;
    selectedClientId: number;
    comment = "";
    apiKey = "";
    isConfigurationOpen = false;
    authType = IntegrationAuthType;
    allocation = IntegrationLicenseAllocation;
    provider = IntegrationProvider;

    // Azure DevOps stays wired per project, so its drawer targets a project instead of the signed-in user.
    devOpsProjects: DevOpsProjectConnectionDto[] = [];
    devOpsSelectedProjectId: number;
    devOpsProjectName = "";
    devOpsProjectUrl = "";
    devOpsPersonalAccessToken = "";
    devOpsDisconnectMode = false;

    // Outlook may have multiple linked mailboxes; one is active for sync/create.
    outlookAccounts: OutlookLinkedAccountDto[] = [];
    outlookManageMode = false;

    // Keka drawer: admin picks the active users the link applies to.
    kekaUsers: UserListDto[] = [];
    kekaSelectedUserIds: number[] = [];
    kekaUsersLoading = false;

    private pendingOAuthProvider: IntegrationProvider;
    private oauthPopup: Window;
    private oauthTimeoutTimer: any;
    private oauthPollTimer: any;
    private oauthInProgress = false;
    private messageHandler: (event: MessageEvent) => void;
    private storageHandler: (event: StorageEvent) => void;

    constructor(
        injector: Injector,
        private _integrationService: IntegrationServiceProxy,
        private _clientService: ClientServiceProxy,
        private _outlookCalendarService: OutlookCalendarServiceProxy,
        private _userService: UserServiceProxy,
        private _ngZone: NgZone,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.messageHandler = (event: MessageEvent) => this.onOAuthMessage(event);
        this.storageHandler = (event: StorageEvent) => {
            if (event.key === OUTLOOK_OAUTH_STORAGE_KEY) {
                this.consumeOutlookOAuthStorageResult();
            }
        };
        window.addEventListener("message", this.messageHandler);
        window.addEventListener("storage", this.storageHandler);
        this.loadCatalog();
        if (!this.readOnly) {
            this.loadClients();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.userId && !changes.userId.firstChange) {
            this.loadCatalog();
        }
    }

    ngOnDestroy(): void {
        window.removeEventListener("message", this.messageHandler);
        window.removeEventListener("storage", this.storageHandler);
        this.resetOAuthPending();
        this.closeConfiguration();
    }

    get canManage(): boolean {
        return !this.readOnly && (!this.userId || this.userId === this.appSession.userId);
    }

    loadCatalog(): void {
        this.loading = true;
        const request =
            this.userId && this.userId !== this.appSession.userId
                ? this._integrationService.getCatalogForUser(this.userId)
                : this._integrationService.getCatalog();

        request
            .pipe(
                finalize(() => {
                    this.loading = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                (result) => {
                    this.catalog = Array.isArray(result) ? [...result] : [];
                    if (this.isOutlook(this.selectedItem) && this.isConfigurationOpen) {
                        this.loadOutlookAccounts();
                    }
                    this.markForCheck();
                },
                () => {
                    this.notify.error(this.l("IntegrationsLoadFailed"));
                },
            );
    }

    openConfiguration(item: IntegrationCatalogItemDto): void {
        if (!this.canManage || item?.isComingSoon || item?.canManage === false) {
            return;
        }

        this.selectedItem = item;
        this.licenseAllocation = IntegrationLicenseAllocation.MyCompany;
        this.selectedClientId = undefined;
        this.comment = item.connection?.comment || "";
        this.apiKey = "";
        this.isConfigurationOpen = true;

        if (this.isDevOps(item)) {
            this.devOpsDisconnectMode = false;
            this.resetDevOpsForm();
            this.loadDevOpsProjects();
        }

        if (this.isOutlook(item)) {
            this.outlookManageMode = false;
            this.loadOutlookAccounts();
        }

        if (this.isKeka(item)) {
            this.kekaSelectedUserIds = [];
            this.loadKekaUsers();
        }
    }

    private loadKekaUsers(): void {
        if (this.kekaUsers.length) {
            return;
        }
        this.kekaUsersLoading = true;
        // Same source as the report pages' Team Members dropdown (empty project list = all users in scope).
        this._userService
            .getActiveUsers()
            .pipe(
                finalize(() => {
                    this.kekaUsersLoading = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                (result) => {
                    this.kekaUsers = result || [];
                    this.markForCheck();
                },
                (err) => {
                    this.kekaUsers = [];
                    this.notify.error(this.getErrorMessage(err, "IntegrationsLoadFailed"));
                },
            );
    }

    closeConfiguration(): void {
        this.apiKey = "";
        this.resetDevOpsForm();
        this.devOpsDisconnectMode = false;
        this.outlookManageMode = false;
        this.outlookAccounts = [];
        this.isConfigurationOpen = false;
        this.clearStaleOverlays();
    }

    private clearStaleOverlays(): void {
        document.documentElement.style.overflow = "";
        document.body.classList.remove("p-overflow-hidden", "modal-open");
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("padding-right");
        document.querySelectorAll(".p-drawer-mask, .p-sidebar-mask, .p-component-overlay, .p-overlay-mask").forEach((mask) => mask.remove());
    }

    connect(): void {
        if (!this.canManage || !this.selectedItem) {
            return;
        }

        // Azure DevOps writes credentials onto the chosen project, not onto an IntegrationConnection.
        if (this.isDevOps(this.selectedItem)) {
            if (this.devOpsDisconnectMode) {
                this.disconnectDevOpsProject();
            } else {
                this.connectDevOpsProject();
            }
            return;
        }

        if (this.isClientAllocation() && !this.selectedClientId) {
            this.notify.warn(this.l("IntegrationsClientRequired"));
            return;
        }

        // Outlook: same connect/disconnect as the old calendar checkbox, triggered from My Profile.
        if (this.selectedItem.provider === IntegrationProvider.Outlook) {
            this.beginOutlookOAuth();
            return;
        }

        if (this.isKeka(this.selectedItem)) {
            this.beginKekaOAuth();
            return;
        }

        if (this.selectedItem.authType === IntegrationAuthType.ApiKey) {
            this.connectWithApiKey();
            return;
        }

        this.beginOAuth();
    }

    disconnect(item: IntegrationCatalogItemDto): void {
        if (!this.canManage) {
            return;
        }

        // DevOps needs to know which project to clear, so it reuses the drawer instead of a plain confirm.
        if (this.isDevOps(item)) {
            this.selectedItem = item;
            this.devOpsDisconnectMode = true;
            this.resetDevOpsForm();
            this.isConfigurationOpen = true;
            this.loadDevOpsProjects();
            return;
        }

        // Outlook: open manage drawer so the user can unlink a specific mailbox (or all via list).
        if (this.isOutlook(item)) {
            this.selectedItem = item;
            this.outlookManageMode = true;
            this.isConfigurationOpen = true;
            this.loadOutlookAccounts();
            return;
        }

        this.message.confirm(this.l("IntegrationsDisconnectConfirm"), this.l("AreYouSure"), (confirmed) => {
            this._ngZone.run(() => {
                if (!confirmed) {
                    return;
                }

                this.connecting = true;
                this._integrationService
                    .disconnect(item.provider)
                    .pipe(
                        finalize(() => {
                            this.connecting = false;
                            this.markForCheck();
                        }),
                    )
                    .subscribe(
                        () => {
                            this.applyDisconnectedState(item.provider);
                            this.notify.success(this.l("IntegrationsDisconnected"));
                            this.loadCatalog();
                        },
                        (err) => this.notify.error(this.getErrorMessage(err, "IntegrationsDisconnectFailed")),
                    );
            });
        });
    }

    isConnected(item: IntegrationCatalogItemDto): boolean {
        return !!item?.connection?.isConnected;
    }

    isDevOps(item: IntegrationCatalogItemDto): boolean {
        return item?.provider === IntegrationProvider.AzureDevOps;
    }

    isOutlook(item: IntegrationCatalogItemDto): boolean {
        return item?.provider === IntegrationProvider.Outlook;
    }

    isKeka(item: IntegrationCatalogItemDto): boolean {
        return item?.provider === IntegrationProvider.Keka;
    }

    get devOpsProjectOptions(): DevOpsProjectConnectionDto[] {
        return this.devOpsDisconnectMode ? this.devOpsProjects.filter((p) => p.isConnected) : this.devOpsProjects;
    }

    onDevOpsProjectChange(): void {
        const selected = this.devOpsProjects.find((p) => p.projectId === Number(this.devOpsSelectedProjectId));
        this.devOpsProjectName = selected?.devProjectName || "";
        this.devOpsProjectUrl = selected?.projectUrl || "";
        this.devOpsPersonalAccessToken = "";
    }

    private resetDevOpsForm(): void {
        this.devOpsSelectedProjectId = undefined;
        this.devOpsProjectName = "";
        this.devOpsProjectUrl = "";
        this.devOpsPersonalAccessToken = "";
    }

    private loadDevOpsProjects(): void {
        this.loading = true;
        this._integrationService
            .getDevOpsProjects()
            .pipe(
                finalize(() => {
                    this.loading = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                (result) => {
                    this.devOpsProjects = result || [];
                    this.markForCheck();
                },
                (err) => {
                    this.devOpsProjects = [];
                    this.notify.error(this.getErrorMessage(err, "IntegrationsLoadFailed"));
                },
            );
    }

    private loadOutlookAccounts(): void {
        this._outlookCalendarService.getLinkedOutlookAccounts(abp.session.userId).subscribe(
            (result) => {
                this.outlookAccounts = result || [];
                this.markForCheck();
            },
            () => {
                this.outlookAccounts = [];
                this.markForCheck();
            },
        );
    }

    isOutlookAccountActive(account: OutlookLinkedAccountDto): boolean {
        if (!account) {
            return false;
        }

        const activeAccounts = (this.outlookAccounts || []).filter((item) => item.isActive);
        if (activeAccounts.length <= 1) {
            return !!account.isActive;
        }

        return activeAccounts[0].id === account.id;
    }

    setActiveOutlookAccount(account: OutlookLinkedAccountDto): void {
        if (!account || this.isOutlookAccountActive(account)) {
            return;
        }

        this.connecting = true;
        this._outlookCalendarService
            .setActiveOutlookAccount(abp.session.userId, account.id)
            .pipe(
                finalize(() => {
                    this.connecting = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                () => {
                    this.notify.success(this.l("IntegrationsOutlookAccountSetActive"));
                    this.loadOutlookAccounts();
                    this.loadCatalog();
                },
                (err) => this.notify.error(this.getErrorMessage(err, "IntegrationsDisconnectFailed")),
            );
    }

    unlinkOutlookAccount(account: OutlookLinkedAccountDto): void {
        if (!account) {
            return;
        }

        this.message.confirm(this.l("IntegrationsOutlookUnlinkAccountConfirm", account.email || ""), this.l("AreYouSure"), (confirmed) => {
            this._ngZone.run(() => {
                if (!confirmed) {
                    return;
                }

                this.connecting = true;
                this._outlookCalendarService
                    .deleteOutlookAccount(abp.session.userId, account.id)
                    .pipe(
                        finalize(() => {
                            this.connecting = false;
                            this.markForCheck();
                        }),
                    )
                    .subscribe(
                        () => {
                            this.notify.success(this.l("IntegrationsOutlookAccountUnlinked"));
                            this._outlookCalendarService.getLinkedOutlookAccounts(abp.session.userId).subscribe((result) => {
                                this.outlookAccounts = result || [];
                                if (!this.outlookAccounts.length) {
                                    this.applyDisconnectedState(IntegrationProvider.Outlook);
                                    this.closeConfiguration();
                                }
                                this.loadCatalog();
                                this.markForCheck();
                            });
                        },
                        (err) => this.notify.error(this.getErrorMessage(err, "IntegrationsDisconnectFailed")),
                    );
            });
        });
    }

    private connectDevOpsProject(): void {
        if (!this.devOpsSelectedProjectId) {
            this.notify.warn(this.l("IntegrationsDevOpsProjectRequired"));
            return;
        }

        if (!this.devOpsProjectName?.trim() || !this.devOpsProjectUrl?.trim() || !this.devOpsPersonalAccessToken?.trim()) {
            this.notify.warn(this.l("IntegrationsDevOpsFieldsRequired"));
            return;
        }

        const input = new ConnectDevOpsProjectInput();
        input.projectId = Number(this.devOpsSelectedProjectId);
        input.devProjectName = this.devOpsProjectName.trim();
        input.projectUrl = this.devOpsProjectUrl.trim();
        input.personalAccessToken = this.devOpsPersonalAccessToken.trim();

        this.connecting = true;
        this._integrationService
            .connectDevOpsProject(input)
            .pipe(
                finalize(() => {
                    this.connecting = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                () => {
                    this.notify.success(this.l("IntegrationsConnectedSuccess"));
                    this.closeConfiguration();
                    this.loadCatalog();
                },
                (err) => this.notify.error(this.getErrorMessage(err, "IntegrationsConnectFailed")),
            );
    }

    private disconnectDevOpsProject(): void {
        if (!this.devOpsSelectedProjectId) {
            this.notify.warn(this.l("IntegrationsDevOpsProjectRequired"));
            return;
        }

        const projectId = Number(this.devOpsSelectedProjectId);
        this.message.confirm(this.l("IntegrationsDisconnectConfirm"), this.l("AreYouSure"), (confirmed) => {
            this._ngZone.run(() => {
                if (!confirmed) {
                    return;
                }
                const input = new DisconnectDevOpsProjectInput();
                input.projectId = projectId;
                this.connecting = true;
                this._integrationService
                    .disconnectDevOpsProject(input)
                    .pipe(
                        finalize(() => {
                            this.connecting = false;
                            this.markForCheck();
                        }),
                    )
                    .subscribe(
                        () => {
                            this.notify.success(this.l("IntegrationsDisconnected"));
                            this.closeConfiguration();
                            this.loadCatalog();
                        },
                        (err) => this.notify.error(this.getErrorMessage(err, "IntegrationsDisconnectFailed")),
                    );
            });
        });
    }

    trackByProvider = (_: number, item: IntegrationCatalogItemDto): IntegrationProvider => item.provider;

    private loadClients(): void {
        this._clientService.getAllClients(ClientType2.AllClients).subscribe(
            (result) => {
                this.clients = result || [];
            },
            () => {
                this.clients = [];
            },
        );
    }

    private connectWithApiKey(): void {
        if (!this.apiKey?.trim()) {
            this.notify.warn(this.l("IntegrationsApiKeyRequired"));
            return;
        }

        const input = new ConnectIntegrationApiKeyInput();
        input.provider = this.selectedItem.provider;
        input.licenseAllocation = this.getSelectedAllocation();
        input.clientId = this.isClientAllocation() ? this.selectedClientId : undefined;
        input.comment = this.comment;
        input.apiKey = this.apiKey.trim();

        this.connecting = true;
        this._integrationService
            .connectWithApiKey(input)
            .pipe(
                finalize(() => {
                    this.connecting = false;
                    this.apiKey = "";
                    this.markForCheck();
                }),
            )
            .subscribe(
                (connection) => {
                    this.applyConnectedState(input.provider, connection);
                    this.notify.success(this.l("IntegrationsConnectedSuccess"));
                    this.closeConfiguration();
                },
                (err) => {
                    this.notify.error(this.getErrorMessage(err, "IntegrationsConnectFailed"));
                },
            );
    }

    private applyConnectedState(provider: IntegrationProvider, connection?: IntegrationConnectionDto): void {
        this.catalog = this.catalog.map((item) => {
            if (item.provider !== provider) {
                return item;
            }

            const nextConnection = connection || item.connection || ({} as IntegrationConnectionDto);
            nextConnection.isConnected = true;
            nextConnection.status = nextConnection.status || 1;
            return Object.assign(new IntegrationCatalogItemDto(), item, {
                connection: Object.assign(new IntegrationConnectionDto(), nextConnection),
            });
        });
        this.markForCheck();
    }

    private applyDisconnectedState(provider: IntegrationProvider): void {
        this.catalog = this.catalog.map((item) => {
            if (item.provider !== provider) {
                return item;
            }

            return Object.assign(new IntegrationCatalogItemDto(), item, {
                connection: undefined,
                connectedAccountCount: item.provider === IntegrationProvider.Outlook ? 0 : item.connectedAccountCount,
            });
        });
        this.markForCheck();
    }

    private beginKekaOAuth(): void {
        const popup = window.open("about:blank", KEK_OAUTH_POPUP_NAME, this.buildPopupFeatures(520, 720));

        if (!popup) {
            this.notify.error(this.l("IntegrationsPopupBlocked"));
            return;
        }

        this.oauthPopup = popup;
        this.connecting = true;
        this.pendingOAuthProvider = IntegrationProvider.Keka;
        this.oauthInProgress = true;
        const input = new BeginIntegrationOAuthInput();
        input.provider = this.selectedItem.provider;
        input.licenseAllocation = this.getSelectedAllocation();
        input.clientId = this.isClientAllocation() ? this.selectedClientId : undefined;
        input.comment = this.comment;
        // The users picked in the drawer; the server stores them as the Keka approvers.
        input.kekaApprovalId = [...(this.kekaSelectedUserIds || [])];

        this._integrationService
            .beginOAuth(input)
            .pipe(
                finalize(() => {
                    this.connecting = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                (result) => {
                    // Keka issues the token on the server. The status URL tells the opener to mark Linked and close this window.
                    if (!result?.authorizationUrl || !this.navigatePopupToUrl(popup, result.authorizationUrl)) {
                        this.safeClosePopup(popup);
                        this.resetOAuthPending();
                        this.notify.error(this.l("IntegrationsConnectFailed"));
                        return;
                    }

                    this.closeConfiguration();
                    this.watchOAuthPopup(popup);
                },
                (err) => {
                    this.safeClosePopup(popup);
                    this.resetOAuthPending();
                    this.notify.error(this.getErrorMessage(err, "IntegrationsConnectFailed"));
                },
            );
    }

    private beginOutlookOAuth(): void {
        clearOutlookOAuthResultFromStorage();

        const popup = window.open("about:blank", OUTLOOK_OAUTH_POPUP_NAME, this.buildPopupFeatures(520, 720));

        if (!popup) {
            this.notify.error(this.l("IntegrationsPopupBlocked"));
            return;
        }

        this.oauthPopup = popup;
        this.connecting = true;
        this.pendingOAuthProvider = IntegrationProvider.Outlook;
        this.oauthInProgress = true;

        this._outlookCalendarService
            .getAuthorizationRequestUrl(undefined)
            .pipe(
                finalize(() => {
                    this.connecting = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                (result) => {
                    const authUrl = result?.outlookAuthUrl;
                    if (!authUrl || !this.navigatePopupToUrl(popup, authUrl)) {
                        this.safeClosePopup(popup);
                        this.resetOAuthPending();
                        this.notify.error(this.l("IntegrationsConnectFailed"));
                        return;
                    }

                    this.closeConfiguration();
                    this.watchOAuthPopup(popup);
                    this.startOutlookOAuthStoragePoll();
                },
                (err) => {
                    this.safeClosePopup(popup);
                    this.resetOAuthPending();
                    this.notify.error(this.getErrorMessage(err, "IntegrationsConnectFailed"));
                },
            );
    }

    private beginOAuth(): void {
        const popup = window.open("about:blank", "intimepro-integration-oauth", this.buildPopupFeatures(520, 720));

        if (!popup) {
            this.notify.error(this.l("IntegrationsPopupBlocked"));
            return;
        }

        this.oauthPopup = popup;
        this.connecting = true;

        const input = new BeginIntegrationOAuthInput();
        input.provider = this.selectedItem.provider;
        input.licenseAllocation = this.getSelectedAllocation();
        input.clientId = this.isClientAllocation() ? this.selectedClientId : undefined;
        input.comment = this.comment;

        this.oauthInProgress = true;
        this._integrationService
            .beginOAuth(input)
            .pipe(
                finalize(() => {
                    this.connecting = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                (result) => {
                    if (!result?.authorizationUrl || !this.navigatePopupToUrl(popup, result.authorizationUrl)) {
                        this.safeClosePopup(popup);
                        this.resetOAuthPending();
                        this.notify.error(this.l("IntegrationsConnectFailed"));
                        return;
                    }

                    this.pendingOAuthProvider = result.provider;
                    this.closeConfiguration();
                    this.watchOAuthPopup(popup);
                },
                (err) => {
                    this.safeClosePopup(popup);
                    this.resetOAuthPending();
                    this.notify.error(this.getErrorMessage(err, "IntegrationsConnectFailed"));
                },
            );
    }

    private watchOAuthPopup(popup: Window): void {
        this.clearOAuthWatch();
        this.oauthPopup = popup;
        this.oauthInProgress = true;

        // Do not poll popup.closed (COOP on Microsoft login). Completion is via postMessage.
        this.oauthTimeoutTimer = setTimeout(
            () => {
                if (!this.oauthInProgress) {
                    return;
                }

                this._ngZone.run(() => {
                    this.safeClosePopup(popup);
                    this.resetOAuthPending();
                    this.notify.warn(this.l("IntegrationsOAuthTimedOut"));
                    this.markForCheck();
                });
            },
            10 * 60 * 1000,
        );
    }

    private onOAuthMessage(event: MessageEvent): void {
        if (!event?.data) {
            return;
        }

        const source = event.data.source;
        if (source !== "intimepro-integrations" && source !== "intimepro-outlook") {
            return;
        }

        const expectedOrigin = source === "intimepro-outlook" ? window.location.origin : this.getApiOrigin();
        if (!expectedOrigin || event.origin !== expectedOrigin) {
            return;
        }

        if (
            this.pendingOAuthProvider !== undefined &&
            event.data.provider != null &&
            Number(event.data.provider) !== Number(this.pendingOAuthProvider)
        ) {
            return;
        }

        this.handleOAuthCompletion(event.data);
    }

    private startOutlookOAuthStoragePoll(): void {
        this.clearOutlookOAuthStoragePoll();
        this.oauthPollTimer = setInterval(() => {
            if (!this.oauthInProgress || this.pendingOAuthProvider !== IntegrationProvider.Outlook) {
                this.clearOutlookOAuthStoragePoll();
                return;
            }

            this.consumeOutlookOAuthStorageResult();
        }, 750);
    }

    private clearOutlookOAuthStoragePoll(): void {
        if (this.oauthPollTimer) {
            clearInterval(this.oauthPollTimer);
            this.oauthPollTimer = undefined;
        }
    }

    private consumeOutlookOAuthStorageResult(): void {
        if (!this.oauthInProgress || this.pendingOAuthProvider !== IntegrationProvider.Outlook) {
            return;
        }

        const payload = readOutlookOAuthResultFromStorage();
        if (!payload) {
            return;
        }

        clearOutlookOAuthResultFromStorage();
        this.handleOAuthCompletion(payload);
    }

    private handleOAuthCompletion(data: any): void {
        const source = data?.source;
        this.safeClosePopup(this.oauthPopup);
        const provider = (data?.provider ?? this.pendingOAuthProvider) as IntegrationProvider;
        this.resetOAuthPending();

        this._ngZone.run(() => {
            if (!data?.success) {
                this.notify.error(data?.message || this.l("IntegrationsConnectFailed"));
                this.loadCatalog();
                this.markForCheck();
                return;
            }

            if (source === "intimepro-outlook") {
                this.completeOutlookOAuth(data.code);
                return;
            }

            this.applyConnectedState(provider);
            this.notify.success(data?.message || this.l("IntegrationsConnectedSuccess"));
        });
    }

    showsTokenUsage(item: IntegrationCatalogItemDto): boolean {
        return item?.provider === IntegrationProvider.GitHubCopilot && !item?.isComingSoon;
    }

    private completeOutlookOAuth(code: string): void {
        if (!code) {
            this.notify.error(this.l("IntegrationsConnectFailed"));
            return;
        }

        this.connecting = true;
        this._outlookCalendarService
            .getAccessTokenFromCode(code, abp.session.userId)
            .pipe(
                finalize(() => {
                    this.connecting = false;
                    this.markForCheck();
                }),
            )
            .subscribe(
                (accessToken) => {
                    if (!accessToken) {
                        this.notify.error(this.l("IntegrationsConnectFailed"));
                        this.loadCatalog();
                        return;
                    }

                    // Same outcome as calendar checkbox check: token stored, events available on My Calendar.
                    this.applyConnectedState(IntegrationProvider.Outlook);
                    this.notify.success(this.l("IntegrationsConnectedSuccess"));
                    this.loadCatalog();

                    this._outlookCalendarService.fetchAndSaveEventsInUserTable(abp.session.userId).subscribe({
                        next: () => undefined,
                        error: (fetchErr) => {
                            this.notify.warn(this.getErrorMessage(fetchErr, "IntegrationsConnectFailed"));
                        },
                    });
                },
                (err) => this.notify.error(this.getErrorMessage(err, "IntegrationsConnectFailed")),
            );
    }

    private navigatePopupToUrl(popup: Window, url: string): boolean {
        if (!popup || !url) {
            return false;
        }

        try {
            popup.document.open();
            popup.document.write(
                `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Connecting</title></head><body>` +
                    `<p>Redirecting to sign-in...</p>` +
                    `<script>window.location.replace(${JSON.stringify(url)});</script>` +
                    `</body></html>`,
            );
            popup.document.close();
            return true;
        } catch {
            try {
                popup.location.replace(url);
                return true;
            } catch {
                return false;
            }
        }
    }

    private safeClosePopup(popup: Window): void {
        if (!popup) {
            return;
        }

        try {
            popup.close();
        } catch {
            // Ignore COOP close failures.
        }
    }

    private resetOAuthPending(): void {
        this.clearOAuthWatch();
        this.clearOutlookOAuthStoragePoll();
        this.oauthInProgress = false;
        this.oauthPopup = undefined;
        this.pendingOAuthProvider = undefined;
    }

    private clearOAuthWatch(): void {
        if (this.oauthTimeoutTimer) {
            clearTimeout(this.oauthTimeoutTimer);
            this.oauthTimeoutTimer = undefined;
        }
    }

    private getErrorMessage(err: any, fallbackKey: string): string {
        const message = err?.error?.error?.message || err?.error?.error?.details || err?.error?.message || err?.message;

        if (typeof message === "string" && message.trim()) {
            return message.trim();
        }

        return this.l(fallbackKey);
    }

    private buildPopupFeatures(width: number, height: number): string {
        const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
        const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
        return `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
    }

    private getApiOrigin(): string {
        try {
            return new URL(AppConsts.remoteServiceBaseUrl).origin;
        } catch {
            return "";
        }
    }

    isClientAllocation(): boolean {
        return Number(this.licenseAllocation) === IntegrationLicenseAllocation.Client;
    }

    private getSelectedAllocation(): IntegrationLicenseAllocation {
        return Number(this.licenseAllocation) as IntegrationLicenseAllocation;
    }
}
